import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Box } from 'components/Box';
import { AnimatedCheckmark } from 'components/Icon/Icon';
import { Body } from 'components/Text';
import TabContext from 'containers/TabContext';
import { colors, transition } from 'theme';

const CheckboxContainer = styled.div<{ hasError?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 35px;
  border-radius: 4px;
  ${(props) =>
    props.hasError &&
    css`
      border: solid 1px ${colors.scarlet};
      box-shadow: 0 0 0 1px ${colors.scarlet};
      padding-right: 15px;
      padding-left: 15px;
    `};
`;
const StyledLabel = styled.label`
  padding-left: 12px;
  cursor: pointer;
`;
const StyledCheckbox = styled.input<{ isTabbing?: boolean }>`
  position: absolute;
  top: -2px;
  left: -2px;
  height: calc(100% + 4px);
  width: calc(100% + 4px);
  padding: 2px;
  margin: 0;
  background: none;
  border: 0;
  cursor: pointer;
  appearance: none;
  &::-ms-check {
    display: none;
  }
  ${(props) =>
    !props.isTabbing &&
    `
    outline: 0;
  `}
  ${(props) =>
    props.disabled &&
    `
    cursor: auto;
  `}
`;
const CheckmarkContainer = styled.div.attrs<{
  $checked?: boolean;
  disabled?: boolean;
}>(({ $checked, disabled }) => ({
  style: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    background: disabled
      ? colors.aluminum
      : $checked
      ? colors.waterBlue
      : colors.platinum,
    boxShadow: `0 0 0 1px rgba(0,0,0,${$checked || disabled ? 0 : 0.2}) inset`,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition: all ${transition};
  border-radius: 2px;
`;
const ToggleCheckmarkContainer = styled.div`
  width: 10px;
  height: 10px;
  svg {
    display: block;
  }
`;
const Toggle = styled.div.attrs<{ $checked?: boolean; disabled?: boolean }>(
  ({ $checked, disabled }) => ({
    style: {
      background: $checked ? colors.waterBlue : colors.silver,
      opacity: disabled ? 0.75 : 1,
    },
  })
)<{ disabled?: boolean; $checked?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transition: all ${transition};
  border-radius: 12px;
  &::before {
    content: '';
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    ${(props) =>
      props.disabled &&
      `
      opacity: 0.75;
    `}
  }
  ${ToggleCheckmarkContainer} {
    left: 7px;
  }
  &::before,
  ${ToggleCheckmarkContainer} {
    position: absolute;
    top: 50%;
    transform: translateY(-50%)
      translateX(${(props) => (props.$checked ? 16 : 0)}px);
    transition: all ${transition};
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Checkbox = ({
  id,
  name,
  label,
  checked,
  hasError,
  onChange,
  toggle,
  disabled,
  large,
  className,
  ...props
}: {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-qa-id'?: string;
  id?: string;
  name?: string;
  label?: ReactNode;
  placeholder?: string | null | undefined;
  required?: boolean;
  value?: (boolean | null | undefined) | (string | null | undefined);
  checked: boolean;
  hasError?: boolean;
  onChange: (arg0: boolean) => void;
  toggle?: boolean;
  disabled?: boolean;
  large?: boolean;
  className?: string;
  valueToChecked?: Record<string, boolean>;
}) => (
  <CheckboxContainer hasError={hasError} className={className}>
    {toggle ? (
      <Box position="relative" width={40} height={24} flex="0 0 auto">
        <Toggle $checked={checked} disabled={disabled}>
          <ToggleCheckmarkContainer>
            {checked && (
              <AnimatedCheckmark
                width="10px"
                height="10px"
                stroke={colors.waterBlue}
                strokeWidth="3px"
                delay={0}
              />
            )}
          </ToggleCheckmarkContainer>
        </Toggle>
      </Box>
    ) : (
      <Box
        position="relative"
        width={large ? 24 : 16}
        height={large ? 24 : 16}
        flex="0 0 auto"
      >
        <CheckmarkContainer
          // @ts-expect-error Fix type
          $checked={checked}
          disabled={disabled}
          className="checkmark-container"
        >
          {checked && (
            <AnimatedCheckmark
              width={large ? 16 : 10}
              height={large ? 16 : 10}
              stroke="white"
              strokeWidth="3px"
              delay={0}
            />
          )}
        </CheckmarkContainer>
      </Box>
    )}
    {label && (
      <StyledLabel htmlFor={id}>
        <Body>{label}</Body>
      </StyledLabel>
    )}
    <TabContext.Consumer>
      {(isTabbing) => (
        // @ts-expect-error Fix type
        <StyledCheckbox
          isTabbing={isTabbing}
          type="checkbox"
          name={name}
          id={id}
          checked={checked}
          onChange={(e) => {
            e.stopPropagation();
            onChange(e.target.checked);
          }}
          disabled={disabled}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    </TabContext.Consumer>
  </CheckboxContainer>
);

// eslint-disable-next-line import/no-default-export
export default Checkbox;
